import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import { getCountryCode } from '../../../utils/countryUtils';
import CollectionStats from './CollectionStats';
import TopPerformingCountries, {
  CountryData,
} from '../hooks/TopPerformingCountries';
import OrdersGraph, { OrderData } from './OrdersGraph';
import PeriodSelector from './PeriodSelector';
import { OrderGraphPeriod } from '../../../api/collections';

const periodLabels: Record<OrderGraphPeriod, string> = {
  [OrderGraphPeriod.DAY]: 'Day',
  [OrderGraphPeriod.WEEK]: 'Week',
  [OrderGraphPeriod.MONTH]: 'Month',
  [OrderGraphPeriod.YEAR]: 'Year',
};

export type AnalyticsTabProps = {
  statsData: {
    totalPurchases: number;
    totalSales: number;
    totalRedemptions?: number;
  };
  sponsored?: boolean;
  countryPerformanceData: Array<{
    country: string;
    percentage: number;
    country_total_order: number;
  }>;
  orderGraphData: Array<{ label: string; count: number }>;
  totalOrders: number;
  layout: 'row' | 'column';
  selectedPeriod: OrderGraphPeriod;
  onPeriodChange: (period: OrderGraphPeriod) => void;
  isLoading: boolean;
  statsError?: boolean;
  countryPerformanceError?: boolean;
  orderGraphError?: boolean;
};

const AnalyticsTab = ({
  statsData,
  sponsored,
  countryPerformanceData,
  orderGraphData,
  totalOrders,
  layout,
  selectedPeriod,
  onPeriodChange,
  isLoading,
  statsError,
  countryPerformanceError,
  orderGraphError,
}: AnalyticsTabProps) => {
  const { spacing } = useTheme();

  const countryData: CountryData[] = useMemo(() => {
    if (!countryPerformanceData) return [];
    return countryPerformanceData
      .filter((country) => getCountryCode(country.country) !== null)
      .map((country) => ({
        countryCode: getCountryCode(country.country)!,
        percentage: `${country.percentage}%`,
        total: country.country_total_order,
      }));
  }, [countryPerformanceData]);

  const orderData: OrderData[] = useMemo(() => {
    if (!orderGraphData) return [];
    return orderGraphData.map((item) => ({
      date: item.label,
      orders: item.count,
    }));
  }, [orderGraphData]);

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: spacing(60),
          width: '100%',
          padding: spacing(3),
        }}
      >
        <DSCircularProgress size={60} />
      </DSBox>
    );
  }

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(3),
        padding: spacing(3),
        width: '100%',
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <CollectionStats
          totalPurchases={statsData.totalPurchases}
          totalSales={statsData.totalSales}
          sponsored={sponsored}
          totalRedemptions={statsData.totalRedemptions}
          hasError={statsError}
        />
        <DSBox>
          <PeriodSelector
            selectedPeriod={selectedPeriod}
            onPeriodChange={onPeriodChange}
            periodLabels={periodLabels}
          />
        </DSBox>
      </DSBox>

      <DSBox
        sx={{
          display: 'flex',
          flexDirection: layout,
          gap: spacing(3),
          width: '100%',
          maxHeight: layout === 'row' ? spacing(50) : 'none',
        }}
      >
        <DSBox sx={{ flex: 1, minWidth: 0 }}>
          <OrdersGraph
            data={orderData}
            totalOrders={totalOrders}
            isEmpty={orderData.length === 0}
            hasError={orderGraphError}
          />
        </DSBox>
        <DSBox sx={{ flex: 1, minWidth: 0 }}>
          <TopPerformingCountries
            data={countryData}
            isEmpty={countryData.length === 0}
            hasError={countryPerformanceError}
          />
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default AnalyticsTab;
